import { useContext, useId, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

import {
    OVERLAY_SIDE_PANEL_EASING,
    OVERLAY_SIDE_PANEL_ENTER_DURATION,
    OVERLAY_SIDE_PANEL_EXIT_DURATION,
} from './overlaySidePanel.constants';
import { OverlaySidePanelBackgroundContext } from './OverlaySidePanelBackgroundContext';

const OVERLAY_MAIN_MIN_WIDTH = 630;
const OVERLAY_SIDE_PANEL_MIN_WIDTH = 320;

interface LayoutState {
    $open: boolean;
}

const Layout = styled.div`
    position: relative;
    isolation: isolate;
    display: flex;
    height: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

const MainContent = styled.main<LayoutState>`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: ${({ $open }) => ($open ? `max(50%, ${OVERLAY_MAIN_MIN_WIDTH}px)` : '100%')};
    min-width: ${OVERLAY_MAIN_MIN_WIDTH}px;
    min-height: 0;
    overflow: auto;
    background: ${theme.colors.background.default};
    transition: width ${({ $open }) => ($open ? OVERLAY_SIDE_PANEL_ENTER_DURATION : OVERLAY_SIDE_PANEL_EXIT_DURATION)}ms
        ${OVERLAY_SIDE_PANEL_EASING};

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }

    &:focus {
        outline: none;
    }
`;

const Panel = styled.aside<LayoutState>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: max(50%, ${OVERLAY_MAIN_MIN_WIDTH}px);
    box-sizing: border-box;
    display: block;
    width: min(50%, calc(100% - ${OVERLAY_MAIN_MIN_WIDTH}px));
    min-width: ${OVERLAY_SIDE_PANEL_MIN_WIDTH}px;
    min-height: 0;
    overflow: hidden;
    border-left: 1px solid ${theme.colors.border.subtle};
    background: ${theme.colors.background.subtle};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition: visibility 0s ${({ $open }) => ($open ? 0 : OVERLAY_SIDE_PANEL_EXIT_DURATION)}ms;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const PanelContent = styled(Box)`
    width: 100%;
    min-width: 0;
    max-width: ${OVERLAY_MAIN_MIN_WIDTH}px;
    margin: 0 auto;
    overflow-x: clip;
    overflow-wrap: anywhere;
`;

interface OverlaySidePanelLayoutProps {
    children: ReactNode;
    panelContent: ReactNode;
    panelOverlay?: ReactNode;
    open: boolean;
    panelId?: string;
}

export const OverlaySidePanelLayout = ({
    children,
    panelContent,
    panelOverlay,
    open,
    panelId,
}: OverlaySidePanelLayoutProps) => {
    const setPanelBackgroundOpen = useContext(OverlaySidePanelBackgroundContext);
    const generatedId = useId();
    const id = panelId ?? generatedId;
    const mainRef = useRef<HTMLElement>(null);
    const panelRef = useRef<HTMLElement>(null);

    useLayoutEffect(() => {
        setPanelBackgroundOpen?.(open);
        if (!open && panelRef.current?.contains(document.activeElement)) {
            const trigger = Array.from(document.querySelectorAll<HTMLElement>('[aria-controls]')).find((element) =>
                element.getAttribute('aria-controls')?.split(' ').includes(id)
            );
            (trigger ?? mainRef.current)?.focus();
        }
        return () => setPanelBackgroundOpen?.(false);
    }, [open, id, setPanelBackgroundOpen]);

    return (
        <Layout>
            <MainContent ref={mainRef} tabIndex={-1} $open={open}>
                {children}
            </MainContent>
            <Panel ref={panelRef} id={id} aria-hidden={!open} inert={!open} $open={open}>
                <Box height="100%" overflow="auto">
                    <PanelContent paddingX={40} paddingY={48}>
                        {panelContent}
                    </PanelContent>
                </Box>
                {panelOverlay}
            </Panel>
        </Layout>
    );
};
